import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import type { ISettingsService, UserSettings } from 'continity-shared-ui';

/**
 * Minimal in-memory {@link ISettingsService} for the standalone Phoenix portal.
 *
 * shared-ui's `TableComponent` (and friends) `inject(SETTINGS_SERVICE)` for regional formats and
 * table preferences. The MAIN portal binds the token to its full `SettingsService` (backed by the
 * user-settings API); this app never provided the token at all, so the first shared-ui table that
 * rendered here (the board's run list) died at construction with NG0201 — the page showed the
 * filters (own component) but neither rows nor an error, while the API happily returned data.
 *
 * This implementation serves static defaults (mirroring the main SettingsService's defaults) and
 * treats updates as session-local no-ops: Phoenix has no user-settings backend of its own (the
 * host-local auth reuses the B2C identity-provider session, not the main portal cookie or settings API — PBI 1466
 * owns the proper dedicated setup). Column/row preferences therefore do not persist across
 * reloads here — acceptable for the status board; wire it to the real settings API when Phoenix
 * gets its own backed user settings.
 */
@Injectable({ providedIn: 'root' })
export class PhoenixSettingsService implements ISettingsService {
  private readonly subject = new BehaviorSubject<UserSettings>({
    regional: {
      numberFormat: 'de-DE',
      dateFormat: 'dd.MM.yyyy',
      unitSystem: 'metric',
    },
    accessibility: {
      highContrast: false,
      reducedAnimations: false,
      largeSpacing: false,
      colorBlindMode: false,
    },
    table: {
      showActionsInline: false,
      rowsPerPage: 10,
      columnVisibility: {},
    },
  });

  readonly settings$: Observable<UserSettings> = this.subject.asObservable();

  getSettings(): UserSettings {
    return this.subject.value;
  }

  updateTableRows(rows: number): Observable<number> {
    const s = this.subject.value;
    this.subject.next({ ...s, table: { ...s.table, rowsPerPage: rows } });
    return of(rows);
  }

  updateTableColumns(tableKey: string, visibleFields: string[]): Observable<{ [key: string]: string[] }> {
    const s = this.subject.value;
    const columnVisibility = { ...(s.table.columnVisibility ?? {}), [tableKey]: visibleFields };
    this.subject.next({ ...s, table: { ...s.table, columnVisibility } });
    return of(columnVisibility);
  }

  updateShowActionsInline(show: boolean): Observable<boolean> {
    const s = this.subject.value;
    this.subject.next({ ...s, table: { ...s.table, showActionsInline: show } });
    return of(show);
  }
}
